<template>
  <edit-dialog
    :dialog="dialog"
    :valid="valid"
    :saving="saving"
    @close="$emit('close')"
    @save="save"
  >
    <template #title>{{ title }}</template>
    <template #body>
      <v-container fluid>
        <v-form v-model="valid" lazy-validation ref="appliedModelForm">
          <v-row no-gutters>
            <v-col cols="12" sm="6" md="3">
              <c-text-field v-model="model.modelCluster" label="Model Cluster" :readonly="readonly" :class="$vuetify.breakpoint.smAndUp ? 'pr-1 required' : 'pb-2 required'" :rules="[$common.rules.required]">
              </c-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <c-text-field v-model="model.svNumber" label="SV Number" :readonly="readonly" :class="$vuetify.breakpoint.smAndUp ? 'pr-1' : 'pb-2'"></c-text-field>
            </v-col>
            <v-col cols="12" sm="5" md="3">
              <c-select
                v-model="model.issuingPartyId"
                label="SV Issuing party"
                :items="organisations"
                :class="$vuetify.breakpoint.smAndUp ? 'px-1' : 'pb-2'"
                :readonly="readonly"
                :clearable="true"
              >

              </c-select>
            </v-col>
            <v-col cols="6" sm="5" md="3">
              <c-text-field v-model="model.countermeasure" label="Countermeasure" :class="$vuetify.breakpoint.smAndUp ? 'px-1' : 'pb-2'" :readonly="readonly"></c-text-field>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="6" sm="2" md="1" class="d-flex align-center">
              <v-checkbox
                hide-details="auto"
                v-model="model.reinvoice"
                label="Reinvoice"
                :class="$vuetify.breakpoint.smAndUp ? 'pl-1 mt-0' : 'pb-2 mt-0'"
                :disabled="readonly"
              ></v-checkbox>
            </v-col>
          </v-row>
          <v-divider class="my-2"></v-divider>
          <v-row no-gutters>
            <v-col cols="6" sm="5">
              <c-text-field v-model="model.labourCost" label="Labour Cost" :class="$vuetify.breakpoint.smAndUp ? 'px-1' : 'pb-2'" :readonly="readonly || model.actualCost" :rules="[$common.rules.decimal, requiredIfNoActualCost]"></c-text-field>
            </v-col>
            <v-col cols="6" sm="5">
              <c-select
                v-model="model.currencyId"
                :items="currencies"
                label="Currency"
                :readonly="readonly || model.actualCost"
                :class="$vuetify.breakpoint.smAndUp ? 'px-1' : 'pb-2'"
                :rules="[requiredIfNoActualCost]"
              ></c-select>
            </v-col>
            <v-spacer></v-spacer>
            <v-col md="2">
              <v-checkbox
                hide-details="auto"
                v-model="model.actualCost"
                label="Actual cost"
                @change="setActualCost"
                :class="$vuetify.breakpoint.smAndUp ? 'pl-1 mt-1' : 'pb-2 mt-1'"
                :disabled="readonly"
              ></v-checkbox>
            </v-col>
          </v-row>
          <v-divider class="my-2"></v-divider>
          <v-row no-gutters>
            <v-col cols="6">
              <c-select
                v-model="model.claimReferenceId"
                :items="claimReferences"
                label="Claim reference"
                :readonly="readonly"
                :disabled="hasNewClaimReference"
                :class="$vuetify.breakpoint.smAndUp ? 'px-1 dotted' : 'pb-2 dotted'"
              ></c-select>
            </v-col>
            <v-col v-if="!readonly" cols="6">
              <c-text-field v-model="model.newClaimReference" label="New claim reference" :class="$vuetify.breakpoint.smAndUp ? 'px-1 dotted' : 'pb-2 dotted'" :readonly="readonly" :rules="[startsWithType]" @input="model.claimReferenceId = 0"></c-text-field>
            </v-col>
          </v-row>
          <v-divider class="my-2"></v-divider>
          <v-row no-gutters v-if="$can('UpdatePSLAndTemplate', esv) || (readonly && (model.pslNumber || model.templateNumber))">
            <v-col cols="6">
              <c-text-field v-model="model.pslNumber" label="PSL Number" :class="$vuetify.breakpoint.smAndUp ? 'px-1' : 'pb-2'" :readonly="readonly"></c-text-field>
            </v-col>
            <v-col cols="6">
              <c-text-field v-model="model.templateNumber" label="Template Number" :class="$vuetify.breakpoint.smAndUp ? 'px-1' : 'pb-2'" :readonly="readonly" :rules="[$common.rules.decimal]"></c-text-field>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
      <v-tabs v-model="activeTab" grow background-color="grey lighten-4">
        <v-tab v-for="tab in tabs" :key="tab">
          {{ tab }}
        </v-tab>
      </v-tabs>
      <v-tabs-items v-model="activeTab">
        <v-tab-item>
          <v-card v-if="readonly">
            <v-card-text class="py-2">
              <v-row>
                <v-col cols="12">
                  <v-data-table
                    dense
                    :headers="headers"
                    :items="selectedModels"
                    item-key="appliedModelsXModelId"
                    :footer-props="{
                      'items-per-page-options': [15, 50, 100, -1]
                    }"
                    :items-per-page="50"
                    :options.sync="options"
                    :server-items-length="totalItems"
                    :loading="selectedModelsLoading"
                  >
                    <template v-slot:header="{ props: { headers } }">
                      <tr>
                        <th v-for="header in headers" :key="header.value">
                          <div
                            v-if="header.type === 'text'"
                            class="mx-1 font-weight-light"
                          >
                            <v-text-field
                              hide-details="auto"
                              dense
                              :label="header.text"
                              clearable
                              outlined
                              v-model="search[header.value]"
                              v-on:input="searchUpdated"
                            ></v-text-field>
                          </div>
                          <div
                            v-else-if="header.type === 'select'"
                            class="mx-1 font-weight-light"
                          >
                            <v-select
                              hide-details="auto"
                              dense
                              outlined
                              :label="header.text"
                              v-model="search[header.value]"
                              :items="headerSelects[header.value]"
                              item-text="description"
                              item-value="id"
                              v-on:change="searchUpdated"
                              clearable
                            ></v-select>
                          </div>
                        </th>
                      </tr>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          <v-card v-else>
            <v-card-text class="py-2">
              <v-row>
                <v-col cols="5">
                  <v-card outlined class="full-height">
                    <v-card-title class="pb-1 d-block">
                      <div class="d-flex align-center">
                        Available Models
                        <v-spacer></v-spacer>
                        <v-progress-circular
                          v-if="availableModelsLoading"
                          indeterminate
                          color="primary"
                          size="20"
                          width="2"
                        ></v-progress-circular>
                      </div>
                      <div class="text-body-2 grey--text text--darken-1 mt-2">
                        <strong class="primary--text">{{ availableCount }}</strong>
                        <span class="ml-1">(loaded <strong>{{ availableFetchedCount }}</strong> of <strong>{{ availableTotalServerItems }}</strong> rows; duplicates hidden)</span>
                        <v-btn
                          v-if="hasMoreAvailableItems && !loadingMore"
                          text
                          color="primary"
                          @click="loadMoreAvailableModels"
                          small
                          class="ml-2"
                        >
                          Load more
                        </v-btn>
                        <v-progress-circular
                          v-if="loadingMore"
                          indeterminate
                          color="primary"
                          size="16"
                          width="2"
                          class="ml-2"
                        ></v-progress-circular>
                      </div>
                    </v-card-title>
                    <v-card-text class="pt-0" style="position: relative;">
                      <v-overlay :value="availableModelsLoading" absolute>
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                      </v-overlay>
                      <div class="d-flex align-center mb-2">
                        <v-text-field v-model="availableSearch" @keyup.enter="getAppliedModelsXModels" label="Search all available models" outlined prepend-inner-icon="mdi-magnify" dense hide-details="auto" class="flex-grow-1 mr-2"></v-text-field>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              small
                              class="grey--text text--darken-1"
                              v-bind="attrs"
                              v-on="on"
                            >
                              mdi-information-outline
                            </v-icon>
                          </template>
                          <span>Search runs on the full available set, not just loaded rows.</span>
                        </v-tooltip>
                      </div>
                      <v-expansion-panels v-model="expansionPanelsValue">
                        <v-expansion-panel v-if="removedModels.length > 0">
                          <v-expansion-panel-header>
                            <v-row>
                              <v-col>
                                Removed (unsaved)
                              </v-col>
                              <v-spacer></v-spacer>
                              <v-col cols="2">
                                <v-chip>
                                  {{ removedModels.length }}
                                </v-chip>
                              </v-col>
                            </v-row>
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <selection :models="removedModels" :clear="clearAdd" ref="addFromRemoved" :emit-update="true"></selection>
                          </v-expansion-panel-content>
                        </v-expansion-panel>

                        <v-expansion-panel v-if="knownInsideAppliedModel.length > 0">
                          <v-expansion-panel-header>
                            <v-row>
                              <v-col>
                                Known inside applied models
                              </v-col>
                              <v-spacer></v-spacer>
                              <v-col cols="2">
                                <v-chip>
                                {{ knownInsideAppliedModel.length }}
                              </v-chip>
                              </v-col>
                            </v-row>
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <selection :models="knownInsideAppliedModel" :clear="clearAdd" ref="addFromAppliedModel" :emit-update="true"></selection>
                            <!-- Load more prompt at bottom of list -->
                            <div v-if="hasMoreAvailableItems" class="text-center py-2 load-more-bottom">
                              <v-btn
                                v-if="!loadingMore"
                                text
                                color="primary"
                                @click="loadMoreAvailableModels"
                                small
                              >
                                Load more models...
                              </v-btn>
                              <v-progress-circular
                                v-if="loadingMore"
                                indeterminate
                                color="primary"
                                size="20"
                              ></v-progress-circular>
                            </div>
                          </v-expansion-panel-content>
                        </v-expansion-panel>

                        <v-expansion-panel v-if="knownInsideDummy.length > 0">
                          <v-expansion-panel-header>
                            <v-row>
                              <v-col>
                                Exists only in dummy applied model
                              </v-col>
                              <v-spacer></v-spacer>
                              <v-col cols="2">
                                <v-chip>
                                {{ knownInsideDummy.length }}
                              </v-chip>
                              </v-col>
                            </v-row>
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <selection :models="knownInsideDummy" :clear="clearAdd" ref="addFromDummy" :emit-update="true"></selection>
                            <!-- Load more prompt at bottom of list -->
                            <div v-if="hasMoreAvailableItems" class="text-center py-2 load-more-bottom">
                              <v-btn
                                v-if="!loadingMore"
                                text
                                color="primary"
                                @click="loadMoreAvailableModels"
                                small
                              >
                                Load more models...
                              </v-btn>
                              <v-progress-circular
                                v-if="loadingMore"
                                indeterminate
                                color="primary"
                                size="20"
                              ></v-progress-circular>
                            </div>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-card-text>
                  </v-card>

                </v-col>
                <v-col align-self="center">
                  <v-btn @click="addToSelection" block outlined class="ma-2">Add</v-btn>
                  <v-btn @click="deleteFromSelection" block outlined class="ma-2">Delete</v-btn>
                </v-col>
                <v-col cols="5">
                  <v-card outlined class="full-height">
                    <v-card-title class="mb-16">
                      Selected Models
                      <v-spacer>
                      </v-spacer>
                      <v-progress-circular
                        v-if="selectedModelsLoading"
                        indeterminate
                        color="primary"
                        size="20"
                        width="2"
                        class="mr-2"
                      ></v-progress-circular>
                      <v-chip>
                        {{ selectedModels.length }}
                      </v-chip>
                    </v-card-title>
                    <v-card-text class="mt-16" style="position: relative;">
                      <v-overlay :value="selectedModelsLoading" absolute>
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                      </v-overlay>
                      <selection :models="selectedModels" :clear="clearDelete" ref="delete" :emit-update="true"></selection>
                    </v-card-text>
                  </v-card>
                  
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-tab-item>
        <v-tab-item>
          <v-card>
            <!-- <v-card-title> </v-card-title> -->
            <v-card-text class="py-2">
              <v-form v-if="!readonly" v-model="newPartValid" ref="newPartForm">
                <v-divider class="mb-2"></v-divider>
                <v-row no-gutters>
                  <v-col cols="3" class="">
                    <v-autocomplete
                      hide-details="auto"
                      v-model="newPart.partId"
                      :items="filteredAvailableParts"
                      label="Part"
                      outlined
                      dense
                      class="required pr-1"
                      :rules="[$common.rules.required]"
                      @change="(partId) => { newPart = { partId }}"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="2">
                    <v-text-field
                      hide-details="auto"
                      v-model="newPart.quantity"
                      label="Quantity"
                      class="required px-1"
                      :rules="[
                        $common.rules.decimal,
                        $common.rules.required,
                        $common.rules.notZero,
                      ]"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                  <v-col cols="2">
                    <v-text-field
                      hide-details="auto"
                      v-model="newPart.productModelGroup"
                      label="Model group"
                      class="px-1"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-text-field
                      hide-details="auto"
                      v-model="newPart.productRemark"
                      label="Remarks"
                      class="px-1"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                  <v-col cols="2">
                    <v-btn
                      color="primary"
                      @click="addPart"
                      width="100%"
                      class="pl-1"
                    >
                      {{ newPart.id ? "Update part" : "Add part" }}
                    </v-btn>
                  </v-col>
                </v-row>
                <v-divider class="mt-2"></v-divider>
              </v-form>
              <v-data-table
                dense
                :headers="partsTable.headers"
                :items="partsTable.items"
                item-key="id"
                :options.sync="partsTable.options"
                :items-per-page="50"
                :server-items-length="partsTable.totalItems"
                @click:row="onPartClicked"
                :item-class="() => 'cursor-pointer'"
                :footer-props="{
                  'items-per-page-options': [15, 50, 100, -1],
                }"
              >
                <template v-slot:item.actions="{ item }" v-if="!readonly">
                  <v-btn icon color="red" @click.stop="removePartFromAppliedModel(item)">
                    <v-icon class="mr-2">mdi-delete</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </template>
  </edit-dialog>
</template>

<script>
var searchTimeOut;
// @ is an alias to /src
import EditDialog from "@/components/common/EditDialog";
import Selection from '@/components/AppliedModel/Selection.vue'
import CTextField from "../common/CTextField.vue";

const distinct = (value, index, self) => {
  return self.findIndex(s => s.modelId === value.modelId && s.serialFrom === value.serialFrom && s.serialTo  === value.serialTo) === index;
}

export default {
  name: "EditAppliedModel",
  components: {
    EditDialog,
    Selection,
    CTextField
},
  props: {
    esvId: Number,
    esv: Object,
    model: Object,
    dialog: Boolean,
    title: String,
    readonly: Boolean,
    forceRefresh: Number
  },
  data: () => ({
    claimReferences: [],
    headers: [
      // { text: "ModelId", value: "modelId" },
      {
        text: "Product Number",
        value: "productNumber",
        type: "text",
        class: "text-subtitle-1 font-weight-bold",
      },
      {
        text: "Serial From",
        value: "serialFrom",
        type: "text",
        class: "text-subtitle-1 font-weight-bold",
      },
      {
        text: "Serial To",
        value: "serialTo",
        type: "text",
        class: "text-subtitle-1 font-weight-bold",
      },
      ],
      selectedModels: [],
      models: [],
      removedModels: [],
      options: {},
    totalItems: 0,
    search: [],
    // Pagination state for available models
    availablePage: 0,
    availablePageSize: 1000,
    availableTotalServerItems: 0,
    loadingMore: false,
    activeTab: null,
    tabs: ["Models", "Parts"],
    partsTable: {
      totalItems: null,
      items: [],
      options: {},
      search: [],
      selected: [],
      headers: [
        { text: "Number", value: "number", class: "text-subtitle-1 font-weight-bold" },
        { text: "Description", value: "description", class: "text-subtitle-1 font-weight-bold" },
        { text: "Quantity", value: "quantity", class: "text-subtitle-1 font-weight-bold" },
        { text: "Cost", value: "cost", class: "text-subtitle-1 font-weight-bold" },
        { text: "Model Group", value: "productModelGroup", class: "text-subtitle-1 font-weight-bold" },
        { text: "Remark", value: "productRemark", class: "text-subtitle-1 font-weight-bold" },
        { text: "Actions", value: "actions", class: "text-subtitle-1 font-weight-bold" },
      ],
      update: false,
    },
    organisations: [],
    valid: false,
    currencies: [],
    availableParts: [],
    newPart: {},
    newPartValid: false,
    availableModelsLoading: false,
    selectedModelsLoading: false,
    saving: false,

    clearAdd: 0,
    clearDelete: 0,

    availableSearch: null,
    expansionPanelsValue: 0,
    previousCost: {}
  }),

  methods: {
    save() {
      if (!this.readonly) {
        if (this.$refs.appliedModelForm.validate() && this.valid) {
          this.model.appliedModelsXModels = this.selectedModels;
          this.model.appliedModelsXParts = this.partsTable.items;
          this.model.esvId = this.esvId;
          this.saving = true;
          this.$api.putAppliedModelUpdate(this.model)
            .then(resp => {
              this.getClaimReferences();
              this.$toast.success("Changes have been saved");

              this.model.id = resp.data;
              if (this.removedModels.length > 0) {
                const existingIds = new Set(this.models.map(m => m.appliedModelsXModelId));
                this.removedModels.forEach(item => {
                  if (!existingIds.has(item.appliedModelsXModelId)) {
                    this.models.push(item);
                  }
                });
                this.removedModels = [];
              }
              this.$emit("updated", true);
            })
            .finally(() => {
              this.saving = false;
            });
        }
      }
      else {
        this.$emit('close');
      }
    },
    addPart() {
      this.$refs.newPartForm.validate();
      if (this.newPartValid) {

        var index = this.partsTable.items.findIndex(p => p.id === this.newPart.id);
        if (index >= 0){
              this.partsTable.items[index].quantity = this.newPart.quantity;
              this.partsTable.items[index].productModelGroup = this.newPart.productModelGroup;
              this.partsTable.items[index].productRemark = this.newPart.productRemark;

              // this.newPart = {};
        }
        else {
          var addedPart = {}
          this.$api.getSparePart(this.newPart.id, this.esvId, this.newPart.partId)
            .then(resp => {
              addedPart = resp.data;
              addedPart.quantity = this.newPart.quantity;
              addedPart.productModelGroup = this.newPart.productModelGroup;
              addedPart.productRemark = this.newPart.productRemark;
              addedPart.appliedModelId = this.model.id ? this.model.id : addedPart.appliedModelId;
              
              var partCount = this.partsTable.items.push(addedPart);
              this.partsTable.totalItems = partCount;

              // this.newPart = {};
            });
        }
        
        this.partsTable.update = false;
      }
    },

    getAppliedModelsXModels() {
      // Reset pagination state for fresh load
      this.availablePage = 0;
      this.models = [];
      this.availableTotalServerItems = 0;
      this.availableModelsLoading = true;

        this.$api
          .getAvailableAppliedModelsXModelsOverview(this.esvId, this.availableSearch, 1, this.availablePageSize, this.model.id)
        .then((resp) => {
          this.models = resp.data.items || [];
          this.availableTotalServerItems = resp.data.totalItems || 0;
          this.availablePage = this.availableTotalServerItems > 0 ? 1 : 0;
        })
        .finally(() => this.availableModelsLoading = false);
    },

      async loadMoreAvailableModels() {
        if (this.loadingMore || !this.hasMoreAvailableItems) return;

        this.loadingMore = true;
        const targetNewItems = this.availablePageSize; // We want to fill a full page of non-duplicates
      let newNonDuplicates = 0;

        try {
          // Keep fetching pages until we have enough non-duplicates or run out of pages.
          // Note: nextPage is used for the request and then persisted to availablePage.
          while (newNonDuplicates < targetNewItems && this.hasMoreServerItems) {
            const nextPage = this.availablePage + 1;
            const resp = await this.$api.getAvailableAppliedModelsXModelsOverview(
              this.esvId,
              this.availableSearch,
              nextPage,
              this.availablePageSize,
              this.model.id
            );

            const pageItems = resp.data.items || [];
            if (pageItems.length === 0) break;

          // Filter to only items not already in models array (by appliedModelsXModelId)
          const existingIds = new Set(this.models.map(m => m.appliedModelsXModelId));
          const newItems = pageItems.filter(item => !existingIds.has(item.appliedModelsXModelId));

            // Add to our models array
            this.models.push(...newItems);
            newNonDuplicates += newItems.length;
            this.availablePage = nextPage;
          }
        } finally {
          this.loadingMore = false;
        }
    },
    getAvailableParts() {
      this.$api
        .getPartsForSelect(this.esvId, this.model.id)
        .then((resp) => (this.availableParts = resp.data));
    },
    getPartsForAppliedModel() {
      if (this.model.id) {
        this.$api
          .getPartsForAppliedModel(this.model.id)
          // .then(resp => this.partsTable.selected = resp.data.items)
          .then((resp) => {
            this.partsTable.items = resp.data.items;
            this.partsTable.totalItems = resp.data.totalItems;
          });
      }
      else {
        this.partsTable.items = [];
        this.partsTable.totalItems = 0;
      }
    },
    getModelsForAppliedModel() {
      if (this.model.id) {
        this.selectedModelsLoading = true;
        this.options.id = this.model.id;
        this.options.search = this.search;
        this.$api
            .getModelsForAppliedModel(this.options, this.search, !this.readonly)
            .then((resp) => {
              if (this.readonly) {
                this.selectedModels = resp.data.items;
                this.totalItems = resp.data.totalItems;
              }
              else {
                this.removedModels = [];
                this.selectedModels.length = 0;
                this.addRangeToSelection(resp.data.items);
              }
            })
            .finally(() => this.selectedModelsLoading = false);
      }
        else {
          this.selectedModels = [];
          this.removedModels = [];
          this.totalItems = 0;
        }
    },
    searchUpdated() {
      clearTimeout(searchTimeOut);
      searchTimeOut = setTimeout(() => {
        this.getModelsForAppliedModel();
      }, 500);
    },
    getOrganisations() {
      this.$api.getFactories().then((resp) => (this.organisations = resp.data));
    },
    getClaimReferences() {
      this.$api
        .getClaimReferences(this.esvId)
        .then((resp) => (this.claimReferences = resp.data));
    },
    getCurrencies() {
      this.$api.getCurrencies().then((resp) => (this.currencies = resp.data));
    },

    onPartClicked(part) {
      this.partsTable.update = true;
      this.$api.getSparePart(part.id).then((resp) => {
        this.newPart = resp.data;
        // this.$refs.newPartForm.resetValidation()
      });
    },

    setActualCost(value) {
      if (value) {
        this.previousCost = {
          currencyId: this.model.currencyId,
          labourCost: this.model.labourCost
        }
        this.model.currencyId = null;
        this.model.labourCost = null;
      }
      else {
        this.model.currencyId = this.previousCost.currencyId;
        this.model.labourCost = this.previousCost.labourCost;
      }
    },

    addToSelection () {
      const addFromRemoved = this.$refs.addFromRemoved ? this.$refs.addFromRemoved.selection : null;
      const addFromAppliedModel = this.$refs.addFromAppliedModel ? this.$refs.addFromAppliedModel.selection : null;
      const addFromDummy = this.$refs.addFromDummy ? this.$refs.addFromDummy.selection : null;
      // const deleteSelection = this.$refs.delete?.selection;
      var selectionToAdd = [];
      if (addFromRemoved) { selectionToAdd.push(...addFromRemoved); }
      if (addFromAppliedModel) { selectionToAdd.push(...addFromAppliedModel); }
      if (addFromDummy) { selectionToAdd.push(...addFromDummy); }

      this.addRangeToSelection(selectionToAdd);
    },
    addRangeToSelection (selectionToAdd) {
      this.selectedModels.push(...selectionToAdd);

      selectionToAdd.forEach(t => {
        const modelIndex = this.models.map(m => m.appliedModelsXModelId).indexOf(t.appliedModelsXModelId);
        if (modelIndex >= 0) {
          this.models.splice(modelIndex, 1);
        }

        const removedIndex = this.removedModels.map(m => m.appliedModelsXModelId).indexOf(t.appliedModelsXModelId);
        if (removedIndex >= 0) {
          this.removedModels.splice(removedIndex, 1);
        }
      });
      // // this.models = this.models.filter(m => this.tempSelectionAdd.findIndex(t => t.appliedModelsXModelId === m.appliedModelsXModelId) > 0);
      // this.tempSelectionAdd.length = 0;
      this.clearAdd += 1;
    },
    availableRangeKey(item) {
      const productNumber = (item.productNumber || "").trim().toUpperCase();
      const serialFrom = (item.serialFrom || "").trim().toUpperCase();
      const serialTo = (item.serialTo || "").trim().toUpperCase();

      return `${productNumber}|${serialFrom}|${serialTo}`;
    },
    deleteFromSelection () {
      const deleteSelection = this.$refs.delete ? this.$refs.delete.selection : null;

      deleteSelection.forEach(t => {
        this.selectedModels.splice(this.selectedModels.map(m => m.appliedModelsXModelId).indexOf(t.appliedModelsXModelId), 1);
      })
      var isEmpty = !this.models.length && !this.removedModels.length;
      const removedIds = new Set(this.removedModels.map(m => m.appliedModelsXModelId));
      deleteSelection.forEach(t => {
        if (!removedIds.has(t.appliedModelsXModelId)) {
          this.removedModels.push(t);
        }
      });

      this.expansionPanelsValue = isEmpty ? 0 : this.expansionPanelsValue;


      this.clearDelete += 1;
    },

    uniqueForRange (value, range) {
      return range.findIndex(x => x.productNumber === value.productNumber && x.serialFrom === value.serialFrom && x.serialTo === value.serialTo) < 0;
    },

    removePartFromAppliedModel(item) {
      var partIndex = this.partsTable.items.findIndex(i => i.partId === item.partId);
      this.partsTable.items.splice(partIndex, 1);
    },

    startsWithType (value) { 
      var serviceRequestTypeName = this.esv && this.esv.serviceRequestType ? this.esv.serviceRequestType.text : '';
      return (!value || value.startsWith(serviceRequestTypeName)) || `Claim reference must start with ${serviceRequestTypeName}`;
    },
    requiredIfNoActualCost () {
      if (!this.model.actualCost && (!this.model.currencyId || !this.model.labourCost)) {
        return 'Currency and labourcost must be filled if actual cost is false'
      }
      return true; 
    }
  },

  mounted() {
    // Update flow initializes from the model.id watcher.
    // Create flow has no model.id yet, so initialize available/selected lists here.
    this.getOrganisations();
    this.getCurrencies();
    if (!this.readonly && !this.model.id) {
      this.getClaimReferences();
      this.getPartsForAppliedModel();
      this.getAvailableParts();
      this.newPart = {};
      this.selectedModels = [];
      this.removedModels = [];
      this.getAppliedModelsXModels();
      this.getModelsForAppliedModel();
    }
  },
  watch: {
    options: {
      handler() {
        this.getModelsForAppliedModel();
      },
    },
    'model.id': {
      immediate: true,
      handler(newId) {
        if (!newId) return; // Skip if model.id is not set yet

        this.getClaimReferences();
        this.getPartsForAppliedModel();
        this.getAvailableParts();
        this.newPart = {};
          // this.partsTable.items = [];
          // this.partsTable.totalItems = 0;

          this.selectedModels = [];
          this.removedModels = [];
          if (!this.readonly){
            this.getAppliedModelsXModels();
            this.getModelsForAppliedModel();
        }
        else {
          this.getModelsForAppliedModel();
        }

      },
    },
    "partsTable.options": {
      handler() {
        this.getAvailableParts();
      },
    },
    forceRefresh: {
        handler() {
          this.getClaimReferences();
          this.getPartsForAppliedModel();
          this.getAvailableParts();
          this.newPart = {};
          // this.partsTable.items = [];
          // this.partsTable.totalItems = 0;

          this.selectedModels = [];
          this.removedModels = [];
          if (!this.readonly){
            this.getAppliedModelsXModels();
            this.getModelsForAppliedModel();
        }
        else {
          this.getModelsForAppliedModel();
        }
      }
    }
    },
    computed: {
      availableCount() {
        const items = [
          ...this.knownInsideAppliedModel,
          ...this.knownInsideDummy,
          ...this.removedModels
        ];
        const keys = new Set(items.map(item => this.availableRangeKey(item)));
        return keys.size;
      },
      availableFetchedCount() {
        return Math.min(
          this.availablePage * this.availablePageSize,
          this.availableTotalServerItems
        );
      },
      knownInsideAppliedModel () {
        return this.models.filter(m => m.appliedModelId !== this.model.id && m.group == 1 && this.uniqueForRange(m, this.selectedModels))
          .filter(distinct);
      },
      knownInsideDummy () {
      return this.models.filter(m =>
        (m.group !== 1 || m.appliedModelId === this.model.id)
        && this.uniqueForRange(m, this.knownInsideAppliedModel)
        && this.uniqueForRange(m, this.selectedModels)
      );
    },

      filteredAvailableParts () {
      return this.availableParts
        .filter(p => 
          this.newPart.partId === p.value ||
          this.partsTable.items.findIndex(amxp => amxp.partId === p.value) < 0
        )
    },
    hasNewClaimReference() {
      return false;// !!this.model.newClaimReference;
    },
    // Check if there are more items on the server (based on pagination)
    hasMoreServerItems() {
      const fetchedSoFar = this.availablePage * this.availablePageSize;
      return fetchedSoFar < this.availableTotalServerItems;
    },
      // Check if we should show the "Load more" button
      hasMoreAvailableItems() {
        return this.hasMoreServerItems;
      }
    }
  };
</script>

<style scoped>
.v-input--checkbox >>> label {
  color: black;
  /* font-size: 14px; */
}

.required >>> fieldset {
  border-left: 3px solid dodgerblue;
}

.dotted >>> fieldset {
  border-left: 3px dotted dodgerblue;
}
.prevent-select {
  user-select: none;
}

.load-more-bottom {
  border-top: 1px dashed #e0e0e0;
  margin-top: 8px;
  background: #fafafa;
}
</style>
